import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { SignOutButton } from "./SignOutButton";
import { SignInButton } from "./SignInButton";
import { useIsAuthenticated } from "@azure/msal-react";
import { Container } from "@mui/material";
import Menu from "@mui/material/Menu/Menu";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import { Link } from "react-router-dom";

const pages = [
    { name: "Home", path: "/", needsAuth: false },
    {
        name: "Applications",
        needsAuth: false,
        children: [
            {
                name: "Gym Companion",
                needsAuth: false,
                children: [
                    { name: "Privacy Policy", path: "/applications/gym-companion/privacy-policy", needsAuth: false },
                ],
            },
        ],
    },
    { name: "About Me", path: "/about", needsAuth: true },
    { name: "Portfolio", path: "/portfolio", needsAuth: true },
];

function isPageVisible(page, isAuthenticated) {
    if (page.needsAuth && !isAuthenticated) {
        return false;
    }
    if (page.children?.length) {
        return page.children.some((child) => isPageVisible(child, isAuthenticated));
    }
    return true;
}

function NestedMenuItem({ item, isAuthenticated, onNavigate }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const visibleChildren = (item.children || []).filter((child) =>
        isPageVisible(child, isAuthenticated)
    );

    if (item.path) {
        return (
            <MenuItem
                component={Link}
                to={item.path}
                onClick={onNavigate}
            >
                {item.name}
            </MenuItem>
        );
    }

    return (
        <>
            <MenuItem
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{ justifyContent: "space-between", minWidth: 180 }}
            >
                {item.name}
                <KeyboardArrowRightIcon fontSize="small" />
            </MenuItem>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                {visibleChildren.map((child) => (
                    <NestedMenuItem
                        key={child.name}
                        item={child}
                        isAuthenticated={isAuthenticated}
                        onNavigate={() => {
                            setAnchorEl(null);
                            onNavigate();
                        }}
                    />
                ))}
            </Menu>
        </>
    );
}

function DesktopNavItem({ page, isAuthenticated }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const visibleChildren = (page.children || []).filter((child) =>
        isPageVisible(child, isAuthenticated)
    );

    if (page.path) {
        return (
            <Button
                component={Link}
                to={page.path}
                sx={{ my: 2, color: "white", display: "block" }}
            >
                {page.name}
            </Button>
        );
    }

    return (
        <>
            <Button
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{ my: 2, color: "white" }}
                endIcon={<KeyboardArrowDownIcon />}
            >
                {page.name}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                {visibleChildren.map((child) => (
                    <NestedMenuItem
                        key={child.name}
                        item={child}
                        isAuthenticated={isAuthenticated}
                        onNavigate={() => setAnchorEl(null)}
                    />
                ))}
            </Menu>
        </>
    );
}

function MobileNavItems({ items, isAuthenticated, depth, onNavigate }) {
    return items
        .filter((item) => isPageVisible(item, isAuthenticated))
        .map((item) => {
            if (item.path) {
                return (
                    <MenuItem
                        key={item.name}
                        component={Link}
                        to={item.path}
                        onClick={onNavigate}
                        sx={{ pl: 2 + depth * 2 }}
                    >
                        <Typography textAlign="left">{item.name}</Typography>
                    </MenuItem>
                );
            }

            return (
                <React.Fragment key={item.name}>
                    <MenuItem disabled sx={{ pl: 2 + depth * 2, opacity: 1 }}>
                        <Typography textAlign="left" color="text.secondary">
                            {item.name}
                        </Typography>
                    </MenuItem>
                    <MobileNavItems
                        items={item.children || []}
                        isAuthenticated={isAuthenticated}
                        depth={depth + 1}
                        onNavigate={onNavigate}
                    />
                </React.Fragment>
            );
        });
}

export default function NavMenuComponent() {
    const isAuthenticated = useIsAuthenticated();
    const logoSrc = `${process.env.PUBLIC_URL}/img/TF.png`;
    const visiblePages = pages.filter((page) => isPageVisible(page, isAuthenticated));

    const [anchorElNav, setAnchorElNav] = React.useState(null);
    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };
    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    return (
        <AppBar position="static">
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    <Box
                        component={Link}
                        to="/"
                        sx={{ display: { xs: "none", md: "flex" }, mr: 2 }}
                    >
                        <Box
                            component="img"
                            sx={{ height: 50, width: 200 }}
                            alt="TF Logo"
                            src={logoSrc}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
                        <IconButton
                            size="large"
                            aria-label="open navigation menu"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{
                                display: { xs: "block", md: "none" },
                            }}
                        >
                            <MobileNavItems
                                items={visiblePages}
                                isAuthenticated={isAuthenticated}
                                depth={0}
                                onNavigate={handleCloseNavMenu}
                            />
                        </Menu>
                    </Box>
                    <Box
                        component={Link}
                        to="/"
                        sx={{ display: { xs: "flex", md: "none" }, mr: 2 }}
                    >
                        <Box
                            component="img"
                            sx={{ height: 50, width: 200 }}
                            alt="TF Logo"
                            src={logoSrc}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
                        {visiblePages.map((page) => (
                            <DesktopNavItem
                                key={page.name}
                                page={page}
                                isAuthenticated={isAuthenticated}
                            />
                        ))}
                    </Box>
                    {isAuthenticated ? <SignOutButton /> : <SignInButton />}
                </Toolbar>
            </Container>
        </AppBar>
    );
}
